import { Elements } from "@stripe/react-stripe-js";
import { useQuery } from "@tanstack/react-query";
import { getStripe } from "@/services/stripe";
import { Button } from "@/components/ui/button";
import { Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { PaymentMethodForm } from "./payment-method-form";
import { useTranslation } from "react-i18next";

interface AddPaymentMethodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddPaymentMethodDialog({
  open,
  onOpenChange,
}: AddPaymentMethodDialogProps) {
  const { t } = useTranslation("app");
  const stripe = useQuery({
    queryKey: ["stripe"],
    queryFn: getStripe,
    enabled: open,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>{t("paymentMethodsScreen.addDialogTitle")}</span>
          <span className="text-small text-default-500 font-normal">
            {t("paymentMethodsScreen.addDialogDescription")}
          </span>
        </ModalHeader>
        <ModalBody className="pb-6">
          {stripe.isError ? (
            <div role="alert" className="space-y-3">
              <p>{t("paymentMethodForm.initErrorDescription")}</p>
              <Button
                onClick={() => void stripe.refetch()}
                disabled={stripe.isFetching}
              >
                {t("actions.retry")}
              </Button>
            </div>
          ) : stripe.data ? (
            <Elements
              stripe={stripe.data}
              options={{
                mode: "setup",
                currency: "cad",
                appearance: {
                  theme: "stripe",
                  variables: {
                    colorPrimary: "#2367A2",
                  },
                },
              }}
            >
              <PaymentMethodForm onSuccess={() => onOpenChange(false)} />
            </Elements>
          ) : (
            <p role="status">{t("paymentMethodForm.loading")}</p>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
